import dayjs from "dayjs";

/**
 * Linear regression trend for Chart.js line overlay.
 * Note: npm package `chartjs-chart-trendline` does not exist; Jacques suggested name — we keep this tiny helper instead.
 */
export function buildLinearTrendline(values) {
    const n = values.length;
    if (n < 2) {
        return values;
    }
    let sumX = 0;
    let sumY = 0;
    let sumXY = 0;
    let sumX2 = 0;
    for (let i = 0; i < n; i += 1) {
        const x = i;
        const y = values[i];
        sumX += x;
        sumY += y;
        sumXY += x * y;
        sumX2 += x * x;
    }
    const denominator = n * sumX2 - sumX * sumX;
    if (denominator === 0) {
        return values;
    }
    const slope = (n * sumXY - sumX * sumY) / denominator;
    const intercept = (sumY - slope * sumX) / n;
    return values.map((_, index) => Number((intercept + slope * index).toFixed(2)));
}

/**
 * Trend for daily booking series: regression uses only days up to referenceDate (excludes future zeros in an open week/range).
 * @param {Record<string, number>} countByDay - YYYY-MM-DD → count
 * @param {import('dayjs').Dayjs} [referenceDate]
 */
export function buildLinearTrendlineForDailySeries(countByDay, referenceDate = dayjs()) {
    const keys = Object.keys(countByDay).sort();
    const values = keys.map((key) => countByDay[key]);
    if (keys.length < 2) {
        return values;
    }

    const regressionPoints = [];
    for (let i = 0; i < keys.length; i += 1) {
        if (!dayjs(keys[i]).isAfter(referenceDate, "day")) {
            regressionPoints.push({ x: i, y: values[i] });
        }
    }
    if (regressionPoints.length < 2) {
        return values;
    }

    let sumX = 0;
    let sumY = 0;
    let sumXY = 0;
    let sumX2 = 0;
    for (const point of regressionPoints) {
        sumX += point.x;
        sumY += point.y;
        sumXY += point.x * point.y;
        sumX2 += point.x * point.x;
    }
    const n = regressionPoints.length;
    const denominator = n * sumX2 - sumX * sumX;
    if (denominator === 0) {
        return values;
    }
    const slope = (n * sumXY - sumX * sumY) / denominator;
    const intercept = (sumY - slope * sumX) / n;
    return keys.map((_, index) => Number((intercept + slope * index).toFixed(2)));
}
